import React, { useState, useEffect, useRef } from 'react';
import { getTeamMembers } from '../services/api';

// Textarea with @mention autocomplete.
//
// The server parses "@Name" out of any comment body and notifies the match, so a
// typed mention has always worked — what was missing in the close-out modal was any
// way to SEE that, or to get the spelling right. Without the dropdown the feature is
// invisible, which reads as broken.
//
// Differences from the copy inside JobComments.js, both for the crew-on-a-phone case:
//   * The list renders in normal flow ABOVE the textarea instead of absolutely
//     positioned below it. The close-out modal is a scroll container, and an absolute
//     dropdown near its bottom edge gets clipped or pushed off-screen.
//   * Rows are full-width with big touch targets, and selection happens on mousedown
//     so the tap isn't swallowed by the textarea losing focus first.
//
// Degrades to a plain textarea when the roster can't be fetched — crews open this in
// dead zones, and a mention lookup must never block a required note.

export default function MentionTextarea({
  value,
  onChange,
  disabled,
  rows = 4,
  placeholder,
  style,
  autoFocus,
}) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [index, setIndex] = useState(0);
  const [caret, setCaret] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getTeamMembers()
      .then((res) => {
        if (cancelled) return;
        setMembers(res.data?.team_members || res.data || []);
      })
      .catch(() => {
        // Offline or failed: no dropdown, plain textarea. A hand-typed @Name still
        // notifies once the note replays.
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = members.filter(
    (m) => m.is_active !== false && (m.name || '').toLowerCase().includes(filter)
  );

  const handleChange = (e) => {
    const next = e.target.value;
    const pos = e.target.selectionStart;
    onChange(next);
    setCaret(pos);

    // Only trigger on an "@" that starts a word, and only until a space is typed —
    // otherwise every email address in a note opens the list.
    const before = next.substring(0, pos);
    const at = before.lastIndexOf('@');
    if (at !== -1) {
      const after = before.substring(at + 1);
      const prev = at > 0 ? before[at - 1] : ' ';
      if ((prev === ' ' || prev === '\n' || at === 0) && !after.includes(' ')) {
        setFilter(after.toLowerCase());
        setOpen(true);
        setIndex(0);
        return;
      }
    }
    setOpen(false);
  };

  const insert = (member) => {
    const before = value.substring(0, caret);
    const after = value.substring(caret);
    const at = before.lastIndexOf('@');
    if (at === -1) return;
    onChange(`${before.substring(0, at)}@${member.name} ${after}`);
    setOpen(false);
    // Caret goes after the inserted name + trailing space, once React has painted.
    setTimeout(() => {
      if (ref.current) {
        const pos = at + member.name.length + 2;
        ref.current.focus();
        ref.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((p) => (p + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((p) => (p - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insert(filtered[index]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div>
      {open && filtered.length > 0 && (
        <div style={{
          border: '2px solid #16a34a', borderRadius: 10, marginBottom: 8,
          maxHeight: 220, overflowY: 'auto', background: '#fff',
        }}>
          {filtered.slice(0, 8).map((m, i) => (
            <div
              key={m.id}
              // mousedown, not click: the textarea blurs on click and the tap is lost.
              onMouseDown={(e) => { e.preventDefault(); insert(m); }}
              onMouseEnter={() => setIndex(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', minHeight: 48, cursor: 'pointer',
                background: i === index ? '#dcfce7' : '#fff',
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                background: '#16a34a', color: '#fff', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {(m.name || '?').charAt(0).toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#111827' }}>{m.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{m.role}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        rows={rows}
        placeholder={placeholder}
        autoFocus={autoFocus}
        style={style}
      />
      {members.length > 0 && (
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
          Type <strong>@</strong> to tag someone and notify them.
        </div>
      )}
    </div>
  );
}
